/* eslint-disable camelcase */
/*
 * WaWiReservierungErfassen.tsx
 * Re-Implementierung von https://huwawi.hudora.de/k/SC10001/r/neu/erfassen
 * Created by Dr. Maximillian Dornseif 2021-12-06 in huWaWi3 24.7.2
 * Copyright (c) 2021-2023 HUDORA GmbH
 */

import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { LinkRender } from '../../lib/hd-link'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { assertIsDefined, assertIsNumber, assertIsString } from 'assertate-debug'
import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import { DateTime } from 'luxon'
import * as R from 'ramda'
import React, { useCallback, useState } from 'react'
import { ResolverResult, useForm } from 'react-hook-form'
import { TextFieldElement } from 'react-hook-form-mui'
import toast from 'react-hot-toast'
import { useMutation } from 'urql'
import { navigate } from 'wouter/use-location'
import * as z from 'zod'

import { RESERVIERUNGSPOSITION_FRAGMENT, RESERVIERUNG_FRAGMENT } from '../../fragments'
import { hdErrorToSentry } from '../../lib/logErrors'
import { EntitySelectComboboxSearch } from '../components/EntitySelectComboboxSearch'
import { OrderlineZufuegen } from '../components/OrderlineZufuegen'
import { AnlieferdatumInput3 } from './AnlieferdatumInput3'
import { ErfassungsPosition } from './ErfassungsPosition'

interface IPos {
  menge: number
  artnr: string
  preis: number
}

const ZEITFENSTER_MIN = 7
const ZEITFENSTER_MAX = 180
const ZEITFENSTER_DEFAULT = 30

const formDefaults = {
  empfaengernr: '',
  name: '',
  anlieferdatum_von: DateTime.now().plus({ days: 3 }).toUTC().toISO(),
  anlieferdatum_bis: DateTime.now()
    .plus({ days: ZEITFENSTER_DEFAULT + 3 })
    .toUTC()
    .toISO(),
  zeitfenster: ZEITFENSTER_DEFAULT,
  auftragsnr_kunde: '',
  infotext_kunde: '',
  infotext_intern: '',
  positionen: [],
}

const schema = z.object({
  empfaengernr: z
    .string()
    .regex(/(KG|SC)[0-9]{5}/)
    .min(7),
  name: z.string().min(4),
  auftragsnr_kunde: z.string().max(20),
  infotext_kunde: z.string().max(1000),
  infotext_intern: z.string().max(1000),
  anlieferdatum_von: z.any(),
  zeitfenster: z.number().min(ZEITFENSTER_MIN).max(ZEITFENSTER_MAX),
  positionen: z.any().array().nonempty().max(50),
})
const zresolver = zodResolver(schema)

export function WaWiReservierungErfassen(props) {
  useTitle('Blockauftrag Erfassen')
  const theme = useTheme()
  const [positionen, setPositionen] = useState<Record<string, IPos>>({})
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    delayError: 250,
    shouldFocusError: true,
    resolver: async (values: any, context: object, options): Promise<ResolverResult> => {
      return await zresolver(values, context, options)
    },
    defaultValues: formDefaults,
  })

  // vom Input Widget <OrderlineZufuegen /> empfangene Orderline zufügen
  const onNewOrderline = useCallback(
    (menge: number, artnr: string, preis: number | undefined) => {
      assertIsString(artnr)
      assertIsNumber(menge)
      const tmpPositionen = { ...positionen }
      if (menge < 1) {
        delete tmpPositionen[artnr]
        setPositionen((prevState) => {
          const newState = { ...prevState }
          delete newState[artnr]
          return newState
        })
      } else {
        tmpPositionen[artnr] = { menge, artnr, preis }
        setPositionen((prevState) => {
          return { ...prevState, [artnr]: { menge, artnr, preis } }
        })
      }
      setValue('positionen', Object.values(tmpPositionen))
    },
    [positionen, setPositionen]
  )
  const [, mutateEntity] = useMutation(RESEVIERUNG_CREATE_MUTATION)

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async (newInput) => {
    newInput.zeitfenster = parseInt(newInput.zeitfenster)
    assertIsNumber(newInput.zeitfenster)
    assertIsDefined(newInput.anlieferdatum_von)
    const result = await mutateEntity({
      input: newInput,
    } as any)
    if (result.error) {
      toast.error(`Fehler: ${result.error.message}`)
      hdErrorToSentry(result.error)
    } else {
      const newDesignator = result.data.hwReservierungsAnlage.designator
      toast.success(
        <React.Fragment>
          Erfolgreich als <LinkRender value={newDesignator} /> angelegt.
        </React.Fragment>
      )
      navigate(`/wawi/ic/rk/${newDesignator}`)
    }
  }

  return (
    <section>
      <Stack spacing={theme?.spacing(1)}>
        <form
          onSubmit={handleSubmit(
            async (data: Object, e?: Event) =>
              await uebertragen({
                ...data,
                positionen: Object.values(positionen),
              }),
            (errors: Object, e?: Event) => console.log(errors)
          )}
        >
          <div>
            <div>
              <h3>Blockauftrag erfassen</h3>
            </div>
            <Paper variant="outlined" sx={{ p: theme?.spacing(1), m: theme?.spacing(1) }}>
              <Stack direction="row" spacing={theme?.spacing(5)}>
                <Box
                  sx={{
                    width: '40%',
                  }}
                >
                  <Stack spacing={theme?.spacing(1)}>
                    <EntitySelectComboboxSearch
                      schemaNames={['HwKunde', 'HwKundenGruppe']}
                      label="Kunde oder Gruppe"
                      placeholder="SC12345"
                      error={!!errors.empfaengernr}
                      helperText={`${errors.empfaengernr ? errors.empfaengernr.message : ' '}`}
                      {...register('empfaengernr')}
                    />
                    <Box>
                      <TextFieldElement
                        name="name"
                        label="Bezeichner/Name"
                        required
                        control={control}
                        variant={'standard'}
                        fullWidth={false}
                      />
                    </Box>
                    <AnlieferdatumInput3 control={control} setValue={setValue} />
                  </Stack>
                </Box>
                <Box>
                  <TextFieldElement
                    name="auftragsnr_kunde"
                    label="Auftrags№ Kunde"
                    control={control}
                    variant={'standard'}
                    fullWidth
                  />
                  <TextFieldElement
                    name="infotext_kunde"
                    label="Infotext Kunde"
                    control={control}
                    variant={'standard'}
                    multiline
                    fullWidth
                  />
                  <TextFieldElement
                    name="infotext_intern"
                    label="Infotext intern"
                    control={control}
                    variant={'standard'}
                    multiline
                    fullWidth
                  />
                </Box>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: theme?.spacing(2), m: theme?.spacing(1) }}>
              <Stack spacing={theme?.spacing(1)}>
                <OrderlineZufuegen
                  key={Object.values(positionen).length}
                  onNewOrderline={onNewOrderline}
                  preisFeld={'Reservierungspreis'}
                />
                {Object.values(positionen).length ? (
                  <Positionen poslist={Object.values(positionen)} />
                ) : (
                  <div>
                    <i>noch keine Position zugefügt</i>
                  </div>
                )}
              </Stack>
            </Paper>
          </div>
          <Stack direction="row" spacing={theme?.spacing(2)}>
            <Button
              type={'submit'}
              variant="contained"
              // disabled={!R.isEmpty(errors)}
              disabled={Object.values(positionen).length < 1 || !R.isEmpty(errors)}
            >
              Blockauftrag anlegen
            </Button>
          </Stack>
        </form>
      </Stack>
      <DevTool control={control} />
    </section>
  )
}

const RESEVIERUNG_CREATE_MUTATION = gql`
  mutation ReservierungAnlegenMutation($input: HwReservierungsAnlageInput!) {
    hwReservierungsAnlage(input: $input) {
      ...reservierungFragment
      positionen {
        ...resevierungspositionFragment
        item {
          id
          name
        }
      }
    }
  }
  ${RESERVIERUNG_FRAGMENT}
  ${RESERVIERUNGSPOSITION_FRAGMENT}
`

export function Positionen({ poslist }) {
  return poslist?.length ? (
    <section>
      <table className="hd-mytable striped">
        <thead>
          <tr>
            <th>Menge</th>
            <th>Item№</th>
            <th>Artikel</th>
            <th>Liste €</th>
            <th>Resevierungspreis €</th>
            <th>Wert €</th>
            <th>heute frei</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {poslist.map((pos) => (
            <ErfassungsPosition key={pos.artnr} pos={pos} />
          ))}
        </tbody>
      </table>
    </section>
  ) : null
}
